import { useEffect } from 'react'

import { type AlbumsSortState } from '@/entities/albums'
import {
  type AlbumModel,
  type AlbumTypeEnumEnum,
  type GetApiAlbumsQueryParams,
} from '@/shared/api'
import { STRINGS } from '@/shared/config'
import { EmptyContent, HidingSpin, TableWrapper } from '@/shared/ui'
import { PrimaryButton } from '@/shared/ui'
import { SearchOutlined } from '@ant-design/icons'
import { Flex, Input, Modal, Table } from 'antd'
import { type TableProps } from 'antd/lib'
import { useUnit } from 'effector-react'

import {
  $albumsSelectionTablePage,
  $albumsSelectionTablePageSize,
  $albumsSelectionTableSearchInputValue,
  $albumsSelectionTableSort,
  $isAlbumSelectionModalOpen,
  $selectedAlbumId,
  AlbumsSelectionTableGate,
  albumIdSelected,
  albumSelectionModalClosed,
  albumSelectionReset,
  albumsSelectionTableFiltersInit,
  albumsSelectionTablePageChanged,
  albumsSelectionTablePageReset,
  albumsSelectionTableQuery,
  albumsSelectionTableSearchChanged,
  albumsSelectionTableSortChanged,
  albumsSelectionTableSortReset,
} from '../model/album-selection-modal.model'

interface AlbumSelectionModalProps {
  authorIds: string[]
  types: AlbumTypeEnumEnum[]
  collectionId: string
  onApply: (albumId: string) => void
}
export function AlbumSelectionModal({
  authorIds,
  types,
  collectionId,
  onApply,
}: AlbumSelectionModalProps) {
  const isOpen = useUnit($isAlbumSelectionModalOpen)
  const close = useUnit(albumSelectionModalClosed)

  const { pending, data, error } = useUnit(albumsSelectionTableQuery)
  const reset = useUnit(albumsSelectionTableQuery.reset)

  const sort = useUnit($albumsSelectionTableSort)
  const changeSort = useUnit(albumsSelectionTableSortChanged)
  const resetSort = useUnit(albumsSelectionTableSortReset)

  const page = useUnit($albumsSelectionTablePage)
  const changePage = useUnit(albumsSelectionTablePageChanged)
  const resetPage = useUnit(albumsSelectionTablePageReset)
  const pageSize = useUnit($albumsSelectionTablePageSize)

  const initAlbumsFilters = useUnit(albumsSelectionTableFiltersInit)

  const search = useUnit($albumsSelectionTableSearchInputValue)
  const changeSearch = useUnit(albumsSelectionTableSearchChanged)

  const selectedId = useUnit($selectedAlbumId)
  const resetSelection = useUnit(albumSelectionReset)
  useEffect(() => {
    const params: GetApiAlbumsQueryParams = {
      AuthorIds: authorIds,
      Types: types,
      ExcludeCollectionId: collectionId,
    }
    initAlbumsFilters(params)
    return () => {
      reset()
      resetSelection()
    }
  }, [reset, initAlbumsFilters, authorIds, types, collectionId, resetSelection])

  const selectRow = useUnit(albumIdSelected)

  const columns: TableProps<AlbumModel>['columns'] = [
    {
      title: 'Наименование',
      width: '50%',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder:
        sort.field === 'name'
          ? sort.order === 'asc'
            ? 'ascend'
            : sort.order === 'desc'
              ? 'descend'
              : null
          : null,
      render: (name, _) => name,
    },
    {
      title: 'Автор',
      width: '50%',
      dataIndex: 'author',
      key: 'author',
      sorter: true,
      sortOrder:
        sort.field === 'author'
          ? sort.order === 'asc'
            ? 'ascend'
            : sort.order === 'desc'
              ? 'descend'
              : null
          : null,
      render: (_, album) => album.authors.map((a) => a.nickname).join(', '),
    },
  ]

  const onTableChange: TableProps<AlbumModel>['onChange'] = (
    pagination,
    _filters,
    sorter,
  ) => {
    // Sort
    if (!Array.isArray(sorter)) {
      const field = sorter.field as string | undefined
      const order = sorter.order as 'ascend' | 'descend' | undefined

      if (field && order) {
        const sortOrder = order === 'ascend' ? 'asc' : 'desc'

        if (sort.field !== field || sort.order !== sortOrder) {
          changeSort({
            field: field as AlbumsSortState['field'],
            order: sortOrder,
          })
          resetPage()
        }
      } else if (sort.field !== null || sort.order !== null) {
        resetSort()
        resetPage()
      }
    }
    // Pagination
    if (pagination.current && pagination.pageSize) {
      if (pagination.current !== page) {
        changePage(pagination.current)
      }
    }
  }

  return (
    <Modal
      centered
      destroyOnHidden
      title="Выбор существующего сезона"
      open={isOpen}
      style={{ minWidth: '50%' }}
      onCancel={() => {
        close()
      }}
      footer={
        <PrimaryButton
          disabled={!selectedId}
          onClick={() => {
            if (typeof selectedId === 'string') onApply(selectedId)
            reset()
            resetSelection()
            close()
          }}
        >
          Добавить
        </PrimaryButton>
      }
    >
      <AlbumsSelectionTableGate />
      <Flex vertical gap={10}>
        <Flex justify="end">
          <Input
            placeholder={STRINGS.placeholder_search_by_name_input}
            allowClear
            addonAfter={<SearchOutlined />}
            value={search || ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const searchString = event.target.value
              changeSearch(searchString)
            }}
            style={{
              width: 300,
            }}
          />
        </Flex>
        <HidingSpin spinning={pending || (!data?.items && !error)}>
          {data?.items && data.items.length > 0 ? (
            <TableWrapper>
              <Table
                sticky
                showSorterTooltip={false}
                rowKey={(record) => record.id}
                bordered
                columns={columns}
                dataSource={data.items}
                pagination={{
                  current: page,
                  pageSize: pageSize,
                  total: data.totalItems,
                  locale: { items_per_page: 'Альбомов на странице' },
                }}
                onChange={onTableChange}
                onRow={(record) => {
                  const isSelected = record.id === selectedId
                  return {
                    onClick: () => {
                      selectRow(record.id)
                    },
                    style: isSelected
                      ? {
                          backgroundColor: '#F9F5FF',
                          color: '#7F45E5',
                          cursor: 'pointer',
                        }
                      : {
                          cursor: 'pointer',
                        },
                  }
                }}
              />
            </TableWrapper>
          ) : search?.length && search?.length > 0 ? (
            <EmptyContent description={STRINGS.nothing_was_found} />
          ) : (
            <EmptyContent description="Нет не выбранных сезонов" />
          )}
        </HidingSpin>
      </Flex>
    </Modal>
  )
}
